import { EmailLinkSource } from '@src/dtos/analytics.dto';
import { isChatBotEnable } from '@src/utils/chat-bot-utils';
import { ComponentNamesForPermission, LevelName, PolicyMode, SmartPolicyTaskStatus } from '@src/utils/enums';
import isNumeric from 'antd/es/_util/isNumeric';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useNavigate } from 'src/utils/use-navigate';
import { INTERVAL_POLLING, SERVICE_STATUS_POLLING } from 'src/utils/constants';
import managedAppsService from '../../../services/managed-apps.service';
import mlRecommendationService from '../../../services/ml-recommendation.service';
import AppHeader from '../../features/app-header/app-header';
import AppsRecommendationModal from '../../features/apps-recommendation-modal/apps-recommendation-modal';
import { DayZero } from '../../features/day-zero/day-zero';
import { ErrorBoundaryComponent } from '../../features/error-boundary/error-boundary';
import MlRecommendationModal from '../../features/ml-recommendation-modal/ml-recommendation-modal';
import { SmartReadyModal } from '../../features/dashboard/smart-ready-modal/smart-ready-modal';
import TrackingWindow from '../../features/tracking-window/tracking-window';
import { ChatBot } from '../../shared/chat-bot/chat-bot';
import Loader from '../../shared/loader/loader';
import { MicroBar } from '../../shared/microbar/microBar';
import usePromiseFeedback from '../../shared/promise-feedback/usePromiseFeedback';
import { ProtectPermissions } from '../../shared/protect-permissions/protect-permissions';
import { StoresContext, useStores } from '../../store';
import './app-content.scss';
import useModalQueue from './modal-queue';
import { MlTipNotificationDto, TipNotificationDto } from '@src/dtos/notification.dto';

export const AppContent = observer(() => {
    const { customerStore, userPreferencesStore, fwVersionStore, notificationStore, uiStore, userStore } = useStores();
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    // Popup queue state
    const { current, enqueue, dequeue } = useModalQueue();

    // State to ensure ML modal pops only once per ML recommendation link source
    const [mlModalShown, setMlModalShown] = useState(false);
    // Smart policy ready modal — rendered at the layout level so it shows on any tab
    const [isSmartReadyModalVisible, setIsSmartReadyModalVisible] = useState(false);

    const navigate = useNavigate();
    const params = useParams();

    useEffect(() => {
        if (customerStore.customerId && customerStore.serviceId && customerStore.selectedLocation?.serviceId === customerStore.serviceId) {
            void Promise.all([
                managedAppsService.getAppRecommendation(customerStore.customerId, customerStore.serviceId),
                mlRecommendationService.getMlRecommendations(customerStore.customerId, customerStore.serviceId)
            ]).then(([appsRes, mlRes]) => {


                if (mlRes && (mlRes.applicationsToBlock?.length > 0 || mlRes.ipsToBlock?.length > 0)) {
                    fwVersionStore.setMlRecommendationData({
                        applicationsToBlock: mlRes.applicationsToBlock ?? [],
                        ipsToBlock: mlRes.ipsToBlock ?? [],
                        serviceId: customerStore.serviceId
                    });
                }

                fwVersionStore.setRecommendedAppData(appsRes);
            });
        }
    }, [customerStore.customerId, customerStore.serviceId, customerStore.selectedLocation]);

    useEffect(() => {
        if (fwVersionStore.recommendedAppData &&
            (fwVersionStore.recommendedAppData.shouldDisplay || uiStore.emailLinkSource === EmailLinkSource.RECOMMEND_APP)) {
            enqueue('apps');
        }
    }, [fwVersionStore.recommendedAppData, uiStore.emailLinkSource]);

    useEffect(() => {
        if (fwVersionStore.mlRecommendationData) {
            enqueue('ml');
        }
    }, [fwVersionStore.mlRecommendationData]);

    useEffect(() => {
        if (
            uiStore.emailLinkSource === EmailLinkSource.RECOMMENDATION_ML &&
            notificationStore.notifications.length > 0 &&
            !mlModalShown
        ) {
            const mlNotification = notificationStore.notifications.find(
                notification => (notification as TipNotificationDto)?.tipNotificationType === 'mlRecommendation'
            );
            if (mlNotification) {
                const notification = mlNotification as MlTipNotificationDto;
                if (notification.applicationsToBlock?.length || notification.ipsToBlock?.length) {
                    fwVersionStore.setMlRecommendationData({
                        applicationsToBlock: notification.applicationsToBlock ?? [],
                        ipsToBlock: notification.ipsToBlock ?? [],
                        serviceId: customerStore.serviceId
                    });
                    setMlModalShown(true);
                }
            }
        }
    }, [uiStore.emailLinkSource, notificationStore.notifications, customerStore.serviceId, mlModalShown]);

    useEffect(() => {
        if (customerStore.selectedLocation?.serviceId === customerStore.serviceId) {
            let timer: any;
            let statusAndMetadataTimer: any;

            if (customerStore.customerId && customerStore.serviceId) {
                loadVersions();
                void customerStore.getServiceStatus();
                void customerStore.getServiceInfo();

                timer = setInterval(loadVersions, INTERVAL_POLLING);
                statusAndMetadataTimer = setInterval(() => {
                    void customerStore.getServiceStatus();
                    void customerStore.getServiceInfo();
                }, SERVICE_STATUS_POLLING);
            }
            return () => {
                clearInterval(timer);
                clearInterval(statusAndMetadataTimer);
            };
        }
    }, [customerStore.customerId, customerStore.serviceId, customerStore.selectedLocation]);

    useEffect(() => {
        if (!isNumeric(params.customerId) || !isNumeric(params.serviceId)) {
            navigate('/', { replace: true });
        }
        void customerStore.setCustomerAndLocation(Number(params.customerId), Number(params.serviceId));
    }, [customerStore.data, params.customerId, params.serviceId]);

    useEffect(() => {
        if (customerStore.selectedLocation?.serviceLevel !== LevelName.BRONZE) {
            if (!userPreferencesStore.isLoading && customerStore.customerId && customerStore.serviceId) {
                promiseFeedbackFactory({
                    promise: Promise.all([userPreferencesStore.load(), userPreferencesStore.loadReportsList()]),
                    notifObj: { show: false }
                });
                void notificationStore.loadWhatsNewLastSeen();
            }

            if (!customerStore.isLoading && !customerStore.data) {
                promiseFeedbackFactory({
                    promise: customerStore.load(),
                    notifObj: { show: false }
                });
            }
        }
    }, [customerStore.serviceId]);

    useEffect(() => {
        if (fwVersionStore.fwVersions && fwVersionStore.isNotDraftExists && fwVersionStore.selectedFwVersion && !fwVersionStore.isSelectedVersionExist) {
            void fwVersionStore.loadVersionData(fwVersionStore.fwVersions[0].externalId, false);
        }
    }, [fwVersionStore.fwVersions, fwVersionStore.isNotDraftExists, fwVersionStore.isSelectedVersionExist]);

    useEffect(() => {
        if (!userStore.isReadOnlyUser && customerStore.isSmartPolicyDraftReady && customerStore.selectedLocation?.smartPolicyTaskStatus === SmartPolicyTaskStatus.COMPLETED &&
            uiStore.smartReadyModalShownByDraftExternalId !== fwVersionStore.draftVersion?.externalId) {
            fwVersionStore.loadVersionData(fwVersionStore.draftVersion!.externalId, false).then(() => {
                uiStore.setSmartReadyModalShownByDraftExternalId(fwVersionStore.draftVersion!.externalId);
                setIsSmartReadyModalVisible(true);
            }).catch(() => setIsSmartReadyModalVisible(false));
        }
    }, [fwVersionStore.fwVersions, customerStore.isSmartPolicyDraftReady, customerStore.selectedLocation?.smartPolicyTaskStatus]);

    const loadVersions = () => {
        if (!fwVersionStore.isLoading) {
            void fwVersionStore.loadVersionsList();
        }
        void notificationStore.loadNotifications();
    };

    const renderContent = () => {
        if (fwVersionStore.containsActiveVersions === false) {
            return <DayZero />;
        }
        if (customerStore.policyMode === PolicyMode.UNDEFINED && fwVersionStore.isOneVersionActive && !customerStore.isSmartProtectionRunning) {
            if (customerStore.areAllServicesPolicyModeUndefined) {
                return (
                    <Navigate
                        to={'/select-customer'}
                        replace={true}
                        state={{
                            welcomeCustomerId: customerStore.customerId
                        }}
                    />
                );
            } else {
                return <Navigate to={`/${customerStore.customerId}/connections`} replace={true} />;
            }
        }

        return <div className={`app-content ${customerStore.policyMode === PolicyMode.SMART_POLICY && fwVersionStore.isOneVersionActive ? 'smart' : ''}`}>
            <AppHeader />
            <MicroBar />
            <TrackingWindow />
            {current === 'apps' && <AppsRecommendationModal
                onClose={() => {
                    fwVersionStore.setRecommendedAppData(undefined);
                    dequeue('apps');
                }}
            /> }
            {current === 'ml' && <MlRecommendationModal
                onClose={() => {
                    dequeue('ml');
                    fwVersionStore.setMlRecommendationData(undefined);
                }}
                hasDraft={!!fwVersionStore.draftVersion}
            />}
            <SmartReadyModal visible={isSmartReadyModalVisible} setIsVisible={setIsSmartReadyModalVisible} />
            <div className="app-body">
                {!!customerStore.customerId && uiStore.systemDataLoaded ?
                    <ErrorBoundaryComponent>
                        <Outlet />
                    </ErrorBoundaryComponent> :
                    <></>}
            </div>
            {isChatBotEnable(customerStore, userStore) && <ChatBot />}
        </div>;
    };

    return (
        fwVersionStore.fwVersions && customerStore.selectedLocation ? <ProtectPermissions componentName={ComponentNamesForPermission.AppProtection}>
            {(hasPermissions) => hasPermissions ? <StoresContext.Provider value={useStores()}>
                {renderContent()}
                {promiseContextHolder}
            </StoresContext.Provider> : <Navigate to={`/${params.customerId}/${params.serviceId}/dashboard-bronze`} />}
        </ProtectPermissions> : <Loader type={'full-screen'} />
    );
});
